import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "./useToast";
import { dispatchTokenExpiredEvent } from "../utils/authEvents";
import {
  createCrawlJob,
  getJob,
  getJobs,
  getSnapshots,
  getSnapshot,
  compareSnapshots,
  downloadSnapshotCsv as downloadSnapshotCsvApi,
  extractOrganizationId,
  wordcloudScopeApi,
  applySnapshotFilters,
  saveExclusions as saveExclusionsApi,
} from "../api/wordcloud";
import type {
  ExclusionConfig,
  WordCloudJob,
  WordCloudSnapshot,
} from "../types/wordcloud";
import type { CrawlScopeApi, CrawlScopeConfig } from "../types/crawlScope";

import { logger } from "../utils/logger";

const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

export const useWordCloud = () => {
  const [snapshots, setSnapshots] = useState<WordCloudSnapshot[]>([]);
  const [activeJobs, setActiveJobs] = useState<Record<string, WordCloudJob>>(
    {},
  );
  // Job ids already being polled — prevents duplicate poll loops when a job
  // shows up both from startCrawl and from the active-jobs fetch.
  const polledJobsRef = useRef(new Set<string>());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();
  const toast = useToast();
  const notifyError = toast.error;
  const isAuthenticated = !!user;
  const activeOrganizationId = extractOrganizationId(user);

  const handleTokenExpired = useCallback(() => {
    dispatchTokenExpiredEvent();
  }, []);

  const ensureActiveOrganization = useCallback(() => {
    if (!user) {
      throw new Error("Authentication required");
    }
    if (!activeOrganizationId) {
      throw new Error("No active organization found for user");
    }
    return activeOrganizationId;
  }, [activeOrganizationId, user]);

  const scopeApi = useMemo<CrawlScopeApi>(
    () => ({
      validateSitemap: (url: string) => {
        ensureActiveOrganization();
        return wordcloudScopeApi.validateSitemap(url);
      },
      discoverSegments: (url: string, selectedSitemaps?: string[]) => {
        ensureActiveOrganization();
        return wordcloudScopeApi.discoverSegments(url, selectedSitemaps);
      },
      discoverSitemaps: (url: string) => {
        ensureActiveOrganization();
        return wordcloudScopeApi.discoverSitemaps!(url);
      },
      discoverFromWebsite: (websiteUrl: string) => {
        ensureActiveOrganization();
        return wordcloudScopeApi.discoverFromWebsite!(websiteUrl);
      },
    }),
    [ensureActiveOrganization],
  );

  const loadSnapshots = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      ensureActiveOrganization();
      const data = await getSnapshots(handleTokenExpired);
      setSnapshots(data);
    } catch (err: unknown) {
      const message = getErrorMessage(err, "Failed to load snapshots");
      setError(message);
      toast.error("Error", message);
    } finally {
      setIsLoading(false);
    }
  }, [ensureActiveOrganization, isAuthenticated, handleTokenExpired, toast]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSnapshots();
    }
  }, [isAuthenticated, loadSnapshots]);

  const startCrawl = async (params: {
    target_url: string;
    is_sitemap: boolean;
    max_urls?: number;
    custom_stop_words?: string[];
    exclusion_config?: ExclusionConfig;
    include_patterns?: string[];
    exclude_patterns?: string[];
    scope?: CrawlScopeConfig;
  }) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      ensureActiveOrganization();
      const response = await createCrawlJob(params, handleTokenExpired);
      toast.success("Job started", "Word cloud generation in progress");
      startPolling(response.job_id);
      return response.job_id;
    } catch (err: unknown) {
      toast.error("Failed to start job", getErrorMessage(err, "Unknown error"));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const removeJob = useCallback((jobId: string) => {
    setActiveJobs((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
  }, []);

  const startPolling = useCallback(
    (jobId: string) => {
      if (polledJobsRef.current.has(jobId)) return;
      polledJobsRef.current.add(jobId);
      const poll = async () => {
        try {
          ensureActiveOrganization();
          const job = await getJob(jobId, handleTokenExpired);
          setActiveJobs((prev) => ({ ...prev, [jobId]: job }));

          if (job.status === "completed") {
            toast.success(
              "Job complete",
              `Word cloud generated for ${job.target_url}`,
            );
            loadSnapshots();
            // Keep the completed card visible briefly, then remove it
            setTimeout(() => removeJob(jobId), 4000);
          } else if (job.status === "failed") {
            toast.error("Job failed", job.message || "Unknown error occurred");
            // Keep failed card visible for 10 s so the user can read the error
            setTimeout(() => removeJob(jobId), 10000);
          } else {
            setTimeout(poll, 3000);
          }
        } catch (err) {
          logger.error("Error polling job:", err);
          setTimeout(poll, 5000);
        }
      };
      poll();
    },
    [
      ensureActiveOrganization,
      handleTokenExpired,
      loadSnapshots,
      removeJob,
      toast,
    ],
  );

  // Pick up jobs created outside this tab (e.g. by the AI agent) so they show
  // in the Active Jobs panel — startCrawl only tracks jobs it started itself.
  const loadActiveJobs = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      ensureActiveOrganization();
      const jobs = await getJobs("pending,running", handleTokenExpired);
      jobs.forEach((job) => {
        setActiveJobs((prev) => ({ ...prev, [job.id]: job }));
        startPolling(job.id);
      });
    } catch (err) {
      logger.error("Error loading active jobs:", err);
    }
  }, [
    ensureActiveOrganization,
    isAuthenticated,
    handleTokenExpired,
    startPolling,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      loadActiveJobs();
    }
  }, [isAuthenticated, loadActiveJobs]);

  const fetchSnapshotDetail = useCallback(
    async (snapshotId: string) => {
      setIsLoading(true);
      try {
        ensureActiveOrganization();
        return await getSnapshot(snapshotId, handleTokenExpired);
      } catch (err: unknown) {
        notifyError(
          "Failed to load snapshot",
          getErrorMessage(err, "Unknown error"),
        );
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [ensureActiveOrganization, handleTokenExpired, notifyError],
  );

  const fetchComparison = async (
    snapshotAId: string,
    snapshotBId: string,
    topN?: number,
  ) => {
    setIsLoading(true);
    try {
      ensureActiveOrganization();
      return await compareSnapshots(
        snapshotAId,
        snapshotBId,
        topN,
        handleTokenExpired,
      );
    } catch (err: unknown) {
      toast.error(
        "Failed to compare snapshots",
        getErrorMessage(err, "Unknown error"),
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const exportSnapshotCsv = async (snapshot: WordCloudSnapshot) => {
    setIsLoading(true);
    try {
      ensureActiveOrganization();
      const blob = await downloadSnapshotCsvApi(
        snapshot.snapshot_id,
        handleTokenExpired,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wordcloud-${snapshot.snapshot_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started", "Word cloud CSV export is downloading");
    } catch (err: unknown) {
      toast.error(
        "Failed to export CSV",
        getErrorMessage(err, "Unknown error"),
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /** Recalculate a snapshot from its saved raw terms. Never re-crawls, never bills. */
  const applyFilters = async (snapshotId: string, config: ExclusionConfig) => {
    setIsLoading(true);
    try {
      ensureActiveOrganization();
      const updated = await applySnapshotFilters(
        snapshotId,
        config,
        handleTokenExpired,
      );
      toast.success(
        "Filters applied",
        "Results recalculated without re-crawling",
      );
      // Keep the cached snapshots list in sync — SnapshotComparison's filter-mismatch
      // warning reads exclusion_config off that list, not off activeSnapshot.
      await loadSnapshots();
      return updated;
    } catch (err: unknown) {
      toast.error(
        "Failed to apply filters",
        getErrorMessage(err, "Unknown error"),
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /** Saving is explicit — applying filters never writes the saved list. */
  const saveExclusionsForDomain = async (
    targetUrl: string,
    config: ExclusionConfig,
  ) => {
    try {
      ensureActiveOrganization();
      await saveExclusionsApi(targetUrl, config, handleTokenExpired);
      toast.success("Saved", "These filters will pre-fill for this domain");
    } catch (err: unknown) {
      toast.error(
        "Failed to save filters",
        getErrorMessage(err, "Unknown error"),
      );
      throw err;
    }
  };

  return {
    snapshots,
    activeJobs,
    isLoading,
    error,
    startCrawl,
    loadSnapshots,
    fetchSnapshotDetail,
    fetchComparison,
    exportSnapshotCsv,
    applyFilters,
    saveExclusionsForDomain,
    scopeApi,
  };
};
